<template>
  <v-dialog v-model="dialogVisible" max-width="700">
    <v-card class="edit-modal">
      <v-card-title class="edit-modal__header">
        <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus" }}</v-icon>
        {{ isEditMode ? "Edit KPI" : "Create New KPI" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="edit-modal__content">
        <v-form ref="form" v-model="formValid" lazy-validation>
          <!-- Name Field -->
          <v-text-field
            v-model="formData.name"
            label="KPI Name"
            prepend-icon="mdi-target"
            :rules="nameRules"
            outlined
            required
          ></v-text-field>

          <!-- Unit Field -->
          <v-text-field
            v-model="formData.unit"
            label="Unit"
            prepend-icon="mdi-ruler"
            outlined
            hint="Measurement unit (e.g., %, kg, units)"
          ></v-text-field>

          <!-- Pillar Field -->
          <v-text-field
            v-model="formData.pillar"
            label="Pillar"
            prepend-icon="mdi-pillar"
            outlined
            hint="Business pillar or category"
          ></v-text-field>

          <!-- Impact Field -->
          <v-textarea
            v-model="formData.impact"
            label="Impact"
            prepend-icon="mdi-flash"
            outlined
            rows="2"
            auto-grow
            hint="Description of the impact"
          ></v-textarea>

          <!-- Description Field -->
          <v-textarea
            v-model="formData.description"
            label="Description"
            outlined
            rows="3"
            auto-grow
            hint="Detailed description of the KPI"
          ></v-textarea>

          <!-- Base Line Data Needed -->
          <v-card class="mb-4">
            <v-card-text class="d-flex align-center justify-space-between">
              <span>Base Line Data Needed</span>
              <v-btn
                @click="
                  formData.baseLineDataNeeded = !formData.baseLineDataNeeded
                "
                :color="formData.baseLineDataNeeded ? 'success' : 'error'"
                outlined
                small
              >
                {{ formData.baseLineDataNeeded ? "Yes" : "No" }}
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Responsibility Definition -->
          <v-text-field
            v-model="formData.responsibilityDefinition"
            label="Responsibility Definition"
            outlined
            hint="Who is responsible for this KPI"
          ></v-text-field>

          <!-- Responsibility Calculation -->
          <v-text-field
            v-model="formData.responsibilityCalculation"
            label="Responsibility Calculation"
            outlined
            hint="How to calculate responsibility"
          ></v-text-field>

          <!-- Data Requirements -->
          <v-textarea
            v-model="formData.dataRequirements"
            label="Data Requirements"
            outlined
            rows="2"
            auto-grow
            hint="What data is needed"
          ></v-textarea>

          <!-- Formula Field -->
          <v-textarea
            v-model="formData.formula"
            label="Formula"
            outlined
            rows="3"
            auto-grow
            hint="Formula for calculating the KPI"
          ></v-textarea>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="edit-modal__actions">
        <v-btn
          v-if="isEditMode"
          color="error"
          text
          @click="$emit('delete', kpi)"
        >
          <v-icon left>mdi-delete</v-icon>
          Delete
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!formValid"
          :loading="saving"
          @click="save"
        >
          <v-icon left v-if="isEditMode">mdi-content-save</v-icon>
          <v-icon left v-else>mdi-plus</v-icon>
          {{ isEditMode ? "Save Changes" : "Create KPI" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "KpiEditModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    kpi: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "save", "delete"],

  data() {
    return {
      formValid: true,
      formData: {
        name: "",
        unit: "",
        pillar: "",
        impact: "",
        description: "",
        baseLineDataNeeded: false,
        responsibilityDefinition: "",
        responsibilityCalculation: "",
        dataRequirements: "",
        formula: "",
      },
      nameRules: [
        (v) => !!v || "KPI name is required",
        (v) => (v && v.length >= 2) || "Name must be at least 2 characters",
      ],
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("close", val);
      },
    },

    isEditMode() {
      return this.kpi && this.kpi.id && !this.kpi.temp;
    },
  },

  methods: {
    close() {
      this.resetForm();
      this.dialogVisible = false;
    },

    resetForm() {
      this.formData = {
        name: "",
        unit: "",
        pillar: "",
        impact: "",
        description: "",
        baseLineDataNeeded: false,
        responsibilityDefinition: "",
        responsibilityCalculation: "",
        dataRequirements: "",
        formula: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    populateForm() {
      if (this.kpi) {
        this.formData = {
          name: this.kpi.name || "",
          unit: this.kpi.unit || "",
          pillar: this.kpi.pillar || "",
          impact: this.kpi.impact || "",
          description: this.kpi.description || "",
          baseLineDataNeeded: this.kpi.baseLineDataNeeded === "True",
          responsibilityDefinition: this.kpi.responsibilityDefinition || "",
          responsibilityCalculation: this.kpi.responsibilityCalculation || "",
          dataRequirements: this.kpi.dataRequirements || "",
          formula: this.kpi.formula || "",
        };
      } else {
        this.resetForm();
      }
    },

    async save() {
      if (this.$refs.form.validate()) {
        this.$emit("save", {
          ...this.kpi,
          ...this.formData,
          baseLineDataNeeded: this.formData.baseLineDataNeeded
            ? "True"
            : "False",
        });
      }
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.populateForm();
      }
    },

    kpi: {
      handler() {
        if (this.value) {
          this.populateForm();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.edit-modal__header {
  background-color: var(--primaryColor);
  color: var(--backgroundColor) !important;
  align-items: center;
}

.edit-modal__content {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
